import { FlexRow } from '@/components/common/Flex';
import { Box, TextProps } from '@chakra-ui/react';
import { isNumber } from '@common/util/types';
import { CalendarDate } from '@internationalized/date';
import { FC, useEffect, useRef } from 'react';
import {
    AriaDateFieldProps,
    useDateField,
    useDateSegment,
    useLocale,
} from 'react-aria';
import {
    DateFieldState,
    DateFieldStateOptions,
    DateSegment,
    useDateFieldState,
} from 'react-stately';
import { AlertTriangle } from '../icons';
import { createCalendar } from './helpers';

type DateFieldProps = AriaDateFieldProps<CalendarDate> &
    Omit<DateFieldStateOptions, 'locale' | 'createCalendar'> & {
        onValidationChange?: (isValid: boolean) => void;
    } & { fontSize?: TextProps['fontSize'] };

export const DateField: FC<DateFieldProps> = ({ fontSize, ...props }) => {
    const { locale } = useLocale();
    const state = useDateFieldState({
        ...props,
        locale,
        createCalendar,
    });

    const ref = useRef(null);
    const { fieldProps } = useDateField(props, state, ref);

    useEffect(() => {
        // The 'invalid'-based logic below is necessary because validationState
        // never seems to get set to 'valid' (tested using react-aria@3.22.0).
        props.onValidationChange?.(state.validationState !== 'invalid');
    }, [props, state]);

    // Track whether year has been fully entered to avoid premature alert icon
    const isYearFullyEntered =
        state.segments.find((s) => s.type === 'year')?.text.length === 4;

    return (
        <FlexRow
            {...fieldProps}
            ref={ref}
            alignItems="center"
            justifyContent="space-between"
        >
            {state.segments.map((s, i) => {
                // Always show month and day with two digits
                // Ideally, react-aria would just let us supply this:
                // `new Intl.DateTimeFormat('en-US', { month: "2-digit", day: "2-digit", year: "numeric" }`
                const segment =
                    !s.isPlaceholder &&
                    (s.type === 'month' || s.type === 'day') &&
                    isNumber(s.value) &&
                    s.value < 10
                        ? { ...s, text: `0${s.text}` }
                        : s;
                return (
                    <DateSegmentBox
                        key={i}
                        segment={segment}
                        state={state}
                        fontSize={fontSize}
                    />
                );
            })}
            {state.validationState === 'invalid' && isYearFullyEntered && (
                <AlertTriangle ml={2} size="sm" color="red.400" />
            )}
        </FlexRow>
    );
};

type DateSegmentBoxProps = {
    segment: DateSegment;
    state: DateFieldState;
    fontSize: TextProps['fontSize'];
};

const DateSegmentBox: FC<DateSegmentBoxProps> = ({
    segment,
    state,
    fontSize,
}) => {
    const ref = useRef(null);
    const { segmentProps } = useDateSegment(segment, state, ref);

    return (
        <Box
            fontSize={fontSize || 'lgr'}
            {...segmentProps}
            outline="none"
            rounded="sm"
            p={0.5}
            color={
                segment.isPlaceholder
                    ? 'gray.300'
                    : !segment.isEditable
                      ? 'gray.600'
                      : 'black'
            }
            _focus={{
                background: 'blue.100',
            }}
            ref={ref}
            className={`segment ${segment.isPlaceholder ? 'placeholder' : ''}`}
        >
            {segment.text}
        </Box>
    );
};
